import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  type ReactElement,
  type Ref,
} from "react";
import { Link } from "react-router";
import {
  CalendarDays,
  ChevronDown,
  Mail,
  Pencil,
  Search,
  TriangleAlert,
  X,
} from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../primitives/collapsible";
import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";
import { Input } from "../../primitives/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../primitives/select";
import type {
  Readiness,
  WorkUnitSummaryItem,
} from "@shared-core/data/api-types";
import type { InFlightBoardSort } from "@shared-core/data/adapter";
import { ReplyComposer } from "./ReplyComposer";
import { plural } from "@shared-core/domain/deadline";
import { ReadinessBadge, readinessBadgeVisible } from "./ReadinessBadge";
import { VisibilityProvenanceBadge } from "../account-allocation/VisibilityProvenanceBadge";
import { AltBoardJobRow } from "./AltBoardJobRow";
import {
  BOARD_SECTION_PREVIEW_COUNT,
  ExpandableSection,
  formatDateWithYear,
  SEVERITY_DOT,
} from "./BoardSectionShell";
import { currentTime } from "@shared-core/domain/current-time";

export type PortfolioBoardGroup =
  | "not_production_ready"
  | "at_risk_late"
  | "due_today"
  | "due_soon"
  | "wip"
  | "recently_completed"
  | "finished_awaiting_close";

/** Lanes whose membership is entirely defined by one readiness value, so
 *  every card in them already carries that meaning via the lane heading
 *  itself — a readiness badge repeating it is pure noise, not information.
 *  `not_production_ready` is the only one today: `READINESS_SQL` (backend)
 *  classifies `non_press` before its catch-all, so every card here is
 *  genuinely `not_production_ready`. The deadline lanes (`due_today`,
 *  `at_risk_late`, …) are urgency-based and mix every readiness value, so
 *  their badges stay meaningful and must not be suppressed.
 *
 *  General rule, not a one-off: `BoardJobRow` suppresses the badge whenever
 *  `job.readiness` matches this map's entry for its lane, so a future
 *  readiness-shaped lane (e.g. a dedicated "Out for proof" lane) is covered
 *  by adding one entry here rather than a new special case at the call
 *  site. */
const READINESS_SHAPED_LANE_READINESS: Partial<
  Record<PortfolioBoardGroup, Readiness>
> = {
  not_production_ready: "not_production_ready",
};

/** Imperative handle so a stat-tile click (see `CommandCentreStatStrip`) can
 *  jump straight to its board lane — scrolled into view and expanded past
 *  its preview/collapsed state — without lifting each section's own
 *  expand/collapse state up into this component. */
export interface PortfolioBoardHandle {
  focusGroup(group: PortfolioBoardGroup): void;
}

interface BoardSectionHandle {
  expandAndScrollIntoView(): void;
}

/**
 * The CSR home's spine (the approved "Portfolio board" design — see
 * docs/features/csr-command-centre/): the CSR's jobs grouped by state —
 * "Not production ready", "Due today", "Due soon", "At risk / Late" open by
 * default, collapsible "In progress (WIP)" and "Recently completed". "Due
 * today" and "Due soon" are separate lanes (not merged) so a CSR can see
 * "jobs due out that day" on its own, per direct user feedback. Each card is
 * quiet and scannable, with comms chips sourced straight off the generated
 * `WorkUnitSummaryItem` (`email_count`/`unread_email_count`/`meeting_count`
 * — real backend counts, no client-side join).
 *
 * "Not production ready" leads the whole board — it's upstream of every
 * deadline lane below it: a job with no production milestone never reached
 * the point where the old deadline buckets would even see it, which is
 * exactly why 211 real in-flight jobs were invisible before this lane
 * existed (see docs/features/csr-command-centre/). Its array is a
 * server-capped, worst-first-by-due-date preview — `notProductionReadyCount`
 * carries the true total for the section heading (see `ExpandableSection`'s
 * `totalCount`).
 *
 * "Due today" and "Due soon" lead the deadline lanes, ahead of "At risk /
 * Late" — per direct user feedback (2026-09-10): a job can appear on both
 * the due-date lanes and the at-risk/late lane, and the due-date lanes are
 * the more actionable "what's going out" list, so they should be seen
 * first.
 *
 * `board` groups are built by the page from the dashboard's already
 * worst-first-sorted arrays. `due_today` and `due_soon` are passed straight
 * through as their own backend arrays; only `at_risk_late` still
 * concatenates two arrays (`overdue`+`at_risk`) — concatenation only, since
 * an overdue job's `required_at` always precedes an at-risk job's, so the
 * combined list stays worst-first without re-sorting (see CsrHome.tsx).
 */
export const PortfolioBoard = forwardRef(function PortfolioBoard(
  {
    board,
    notProductionReadyCount,
  }: {
    board: Record<PortfolioBoardGroup, WorkUnitSummaryItem[]>;
    /** `summary_counts.not_production_ready_count` — the true total behind
     *  `board.not_production_ready`'s server-capped preview array. */
    notProductionReadyCount: number;
  },
  ref: Ref<PortfolioBoardHandle>,
) {
  const sectionRefs = useRef<
    Partial<Record<PortfolioBoardGroup, BoardSectionHandle>>
  >({});

  useImperativeHandle(ref, () => ({
    focusGroup(group) {
      sectionRefs.current[group]?.expandAndScrollIntoView();
    },
  }));

  const registerSection =
    (group: PortfolioBoardGroup) => (handle: BoardSectionHandle | null) => {
      if (handle) sectionRefs.current[group] = handle;
      else delete sectionRefs.current[group];
    };

  return (
    <div className="space-y-6">
      <BoardSection
        ref={registerSection("not_production_ready")}
        title="Not production ready"
        group="not_production_ready"
        jobs={board.not_production_ready}
        totalCount={notProductionReadyCount}
        emptyMessage="Nothing is waiting to enter production."
      />
      <BoardSection
        ref={registerSection("due_today")}
        title="Due today"
        group="due_today"
        jobs={board.due_today}
        emptyMessage="Nothing due today."
      />
      <BoardSection
        ref={registerSection("due_soon")}
        title="Due soon"
        group="due_soon"
        jobs={board.due_soon}
        emptyMessage="Nothing due soon."
      />
      <BoardSection
        ref={registerSection("at_risk_late")}
        title="At risk / Late"
        group="at_risk_late"
        jobs={board.at_risk_late}
        emptyMessage="No at-risk or late jobs."
      />
      <CollapsibleBoardSection
        ref={registerSection("wip")}
        title="In progress (WIP)"
        group="wip"
        jobs={board.wip}
        emptyMessage="No other jobs in progress."
      />
      <CollapsibleBoardSection
        ref={registerSection("recently_completed")}
        title="Recently completed"
        group="recently_completed"
        jobs={board.recently_completed}
        emptyMessage="No recent completions."
      />
      <CollapsibleBoardSection
        ref={registerSection("finished_awaiting_close")}
        title="Finished — awaiting close"
        group="finished_awaiting_close"
        jobs={board.finished_awaiting_close}
        emptyMessage="Nothing finished is waiting to be closed."
      />
    </div>
  );
});

/**
 * WIP-focused alternative to `PortfolioBoard` — used by `CsrHomeAlt` (an
 * alternative CSR home layout under side-by-side evaluation, see
 * CsrHomeAlt.tsx), which drops the deadline/readiness lanes above WIP and
 * makes the page's main content one combined, worst-first list of every
 * in-flight job instead (`dashboard.in_flight_board` — csr-dashboard-
 * alt-screen; the user's own name for this list is "the WIP list", so the
 * heading stays "In progress (WIP)" even though its population is no
 * longer WIP-only). Unlike `PortfolioBoard`'s own WIP lane
 * (`CollapsibleBoardSection`, closed by default with no cap), this renders
 * every job directly, always expanded — the point of giving it the
 * freed-up column space. `inFlightBoardTotal` is the true count behind a
 * server-side cap (see `csr_home_query.get_in_flight_board`); when it
 * exceeds `inFlightBoard.length`, a "Showing N of M" banner links out to
 * the full filtered list on the Jobs page — expanding in place isn't
 * possible since the remainder was never fetched. "Recently completed" and
 * "Finished — awaiting close" keep their ordinary collapsed-by-default
 * presentation unchanged; nothing about those two lanes was in scope, only
 * what leads them.
 */
const _SORT_OPTIONS: { value: InFlightBoardSort; label: string }[] = [
  { value: "worst_first", label: "Worst first" },
  { value: "status", label: "Status" },
  { value: "stage", label: "Stage" },
];

export function WipFocusedBoard({
  inFlightBoard,
  inFlightBoardTotal,
  viewAllJobsHref,
  recentlyCompleted,
  finishedAwaitingClose,
  query,
  onQueryChange,
  sort,
  onSortChange,
  isSearchActive,
  searchLoading,
}: {
  inFlightBoard: WorkUnitSummaryItem[];
  /** True count behind `inFlightBoard`'s server-side cap — see
   *  `WorkUnitSummaryItem`'s sibling field on the API schema. While a
   *  search (`query`/`sort` not default) is active, this is the search's
   *  own matched-row total instead — see `GET
   *  /views/csr-home/in-flight-board`. */
  inFlightBoardTotal: number;
  /** Jobs-page URL ("mine" + the work view) for the "view all" banner
   *  shown when the cap is reached. */
  viewAllJobsHref: string;
  recentlyCompleted: WorkUnitSummaryItem[];
  finishedAwaitingClose: WorkUnitSummaryItem[];
  /** Current search box text (controlled — the parent owns debouncing and
   *  the URL-sync, see `CsrHomeAlt`). */
  query: string;
  onQueryChange: (value: string) => void;
  sort: InFlightBoardSort;
  onSortChange: (value: InFlightBoardSort) => void;
  /** True once `query` (debounced) or `sort` differs from the defaults —
   *  drives the "No jobs match" empty state wording and the loading
   *  indicator, both of which only make sense once a search is live. */
  isSearchActive: boolean;
  searchLoading: boolean;
}) {
  const hasMoreThanFetched = inFlightBoardTotal > inFlightBoard.length;
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
          In progress (WIP)
          <span className="ml-2 font-normal normal-case tracking-normal text-foreground">
            ({inFlightBoardTotal})
          </span>
        </h2>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="relative max-w-md flex-1 min-w-[220px]">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              placeholder="Search your jobs by job number, customer or title"
              aria-label="Search your in-progress jobs"
              className="pl-8 pr-8"
            />
            {query && (
              <button
                type="button"
                onClick={() => onQueryChange("")}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="size-4" />
              </button>
            )}
          </div>
          <Select
            value={sort}
            onValueChange={(v) => onSortChange(v as InFlightBoardSort)}
          >
            <SelectTrigger className="h-9 text-sm w-[140px]" aria-label="Sort">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {_SORT_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {isSearchActive && searchLoading ? (
          <p className="text-sm text-muted-foreground py-2">Searching…</p>
        ) : inFlightBoard.length === 0 ? (
          <p className="text-sm text-muted-foreground italic py-2">
            {isSearchActive && query
              ? `No jobs match "${query}"`
              : "No other jobs in progress."}
          </p>
        ) : (
          <div className="space-y-1">
            {inFlightBoard.map((job) => (
              <AltBoardJobRow key={job.id} job={job} />
            ))}
          </div>
        )}
        {hasMoreThanFetched && (
          <p className="mt-2 text-sm text-muted-foreground">
            Showing {inFlightBoard.length} of {inFlightBoardTotal} —{" "}
            <Link to={viewAllJobsHref} className="text-primary hover:underline">
              view all in Jobs
            </Link>
          </p>
        )}
      </div>
      <CollapsibleBoardSection
        title="Recently completed"
        group="recently_completed"
        jobs={recentlyCompleted}
        emptyMessage="No recent completions."
        renderItem={(job) => <AltBoardJobRow key={job.id} job={job} />}
      />
      <CollapsibleBoardSection
        title="Finished — awaiting close"
        group="finished_awaiting_close"
        jobs={finishedAwaitingClose}
        emptyMessage="Nothing finished is waiting to be closed."
        renderItem={(job) => <AltBoardJobRow key={job.id} job={job} />}
      />
    </div>
  );
}

const BoardSection = forwardRef(function BoardSection(
  {
    title,
    group,
    jobs,
    emptyMessage,
    totalCount,
  }: {
    title: string;
    group: PortfolioBoardGroup;
    jobs: WorkUnitSummaryItem[];
    emptyMessage: string;
    /** True total behind a server-capped preview array — see
     *  `ExpandableSection`'s `totalCount`. Omit for a lane whose array is
     *  already the complete list. */
    totalCount?: number;
  },
  ref: Ref<BoardSectionHandle>,
) {
  const [expanded, setExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    expandAndScrollIntoView() {
      setExpanded(true);
      containerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    },
  }));

  return (
    <div ref={containerRef}>
      <ExpandableSection
        title={title}
        items={jobs}
        emptyMessage={emptyMessage}
        previewCount={BOARD_SECTION_PREVIEW_COUNT}
        expanded={expanded}
        onToggleExpanded={() => setExpanded((current) => !current)}
        totalCount={totalCount}
        renderItem={(job) => (
          <BoardJobRow key={job.id} job={job} group={group} />
        )}
      />
    </div>
  );
});

const CollapsibleBoardSection = forwardRef(function CollapsibleBoardSection(
  {
    title,
    group,
    jobs,
    emptyMessage,
    renderItem,
  }: {
    title: string;
    group: PortfolioBoardGroup;
    jobs: WorkUnitSummaryItem[];
    emptyMessage: string;
    /** Defaults to the original board's `BoardJobRow` (the unchanged
     *  `/home/csr` page). `WipFocusedBoard` (the alt page) passes
     *  `AltBoardJobRow` instead, so the two pages' "Recently completed" /
     *  "Finished — awaiting close" sections can share this chrome while
     *  rendering a different card. */
    renderItem?: (job: WorkUnitSummaryItem) => ReactElement;
  },
  ref: Ref<BoardSectionHandle>,
) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    expandAndScrollIntoView() {
      setOpen(true);
      containerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    },
  }));

  return (
    <div ref={containerRef}>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger className="flex items-center gap-2 w-full text-left mb-2 group">
          <ChevronDown
            className={`size-4 text-muted-foreground transition-transform ${open ? "rotate-0" : "-rotate-90"}`}
          />
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            {title}
            <span className="ml-2 font-normal normal-case tracking-normal text-foreground">
              ({jobs.length})
            </span>
          </h2>
        </CollapsibleTrigger>
        <CollapsibleContent>
          {jobs.length === 0 ? (
            <p className="text-sm text-muted-foreground italic py-2">
              {emptyMessage}
            </p>
          ) : (
            <div className="space-y-1.5 pl-6">
              {jobs.map((job) =>
                renderItem ? (
                  renderItem(job)
                ) : (
                  <BoardJobRow key={job.id} job={job} group={group} />
                ),
              )}
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
});

/** Whole-day difference between a job's due date and today, measured on date
 *  boundaries so it matches the backend's `due_at::date` urgency bucketing
 *  (a job due later today is 0, not −1). Negative = overdue. */
function dueDayDiffFromToday(requiredAt: string): number {
  const due = new Date(requiredAt);
  due.setHours(0, 0, 0, 0);
  const today = currentTime();
  today.setHours(0, 0, 0, 0);
  return Math.round((due.getTime() - today.getTime()) / 86_400_000);
}

/** Human label for why a finished job is still open, keyed by close_state. */
const CLOSE_STATE_LABEL: Record<
  NonNullable<WorkUnitSummaryItem["close_state"]>,
  string
> = {
  awaiting_despatch: "awaiting despatch",
  awaiting_invoice: "awaiting invoice",
  invoiced: "invoiced — closing",
  into_stock: "booked into stock",
};

/** Short triage flag for a board row's deadline badge, computed from fields
 *  already on `WorkUnitSummaryItem` (rag, required_at, completion_state,
 *  close_state, needed_by_year_suspect) — presentational formatting only,
 *  same spirit as CsrHome.tsx's `formatDue`, not a business decision. This
 *  covers the deadline axis only; the *reason* a job carries an open alert
 *  or is stuck is a separate one-line fact rendered below the badges — see
 *  `boardReasonLine`.
 *
 *  Two independent facts feed this, and they must not be conflated:
 *  - **Deadline lateness** — `required_at` vs today, date-only. Only this
 *    decides the OVERDUE badge and the day count; a job due today or later
 *    is never OVERDUE, however severe its alerts.
 *  - **`job.rag`** — the work-alert rollup (`bool_or(severity = 'red')` over
 *    uncleared alerts). It carries no deadline input at all, so it only
 *    escalates this badge's *severity* (a red alert never renders quieter
 *    than amber) — it no longer mints its own word. Earlier this function
 *    rendered a bare "ALERT" / "ALERT · due in Nd" badge for a red alert
 *    with no matching deadline word; that reason now lives in
 *    `boardReasonLine` instead, named from `job.top_alert_label`, which is
 *    strictly better than a bare "ALERT". (Correction: the commits that
 *    introduced this function's deadline/rag split cited 48 jobs wrongly
 *    badged OVERDUE; that figure came from a query that forgot
 *    `cleared_at IS NULL` and counted cleared historical alerts. Correctly
 *    measured, 26 in-flight jobs carry `rag = red`, of which 22 are
 *    genuinely overdue, 4 were due today, and 0 were due in the future. The
 *    underlying bug — `_phase_deadline_at_risk.py` escalating to red off a
 *    raw `due_at` timestamp rather than a cleared/uncleared alert — has
 *    since been fixed; this function's own deadline/rag split was already
 *    correct.)
 *
 *  Two overrides sit ahead of both:
 *  - **Finished jobs never go red.** A job carrying a `close_state`
 *    (produced-awaiting-despatch or delivered-awaiting-invoice) is rendered
 *    neutral with its close-state reason, whatever group it lands in (the
 *    finished-awaiting-close section is the usual home, but this also guards
 *    a finished job that slips into a risk bucket).
 *  - **A suspect needed-by year** shows a quiet "check in Tharstern" hint
 *    (`kind: "hint"`) instead of a large "OVERDUE Nd" badge, because the day
 *    delta is computed off a date that is probably a data-entry slip. */
export function boardFlag(
  job: WorkUnitSummaryItem,
  group: PortfolioBoardGroup,
): {
  reason: string | null;
  severity: "red" | "amber" | "neutral";
  kind: "badge" | "hint";
} {
  // The recently-completed and WIP lenses keep their own labels: a terminal
  // job shows its completion_state ("Delivered"/"Invoiced"), not a
  // close_state ("awaiting invoice"). So those group checks sit ahead of the
  // close_state override, which is scoped to the still-open lanes (the
  // finished-awaiting-close section, plus guarding a finished job that
  // slips into a risk bucket — it must never render red).
  if (group === "recently_completed") {
    return {
      reason: job.completion_state ?? null,
      severity: "neutral",
      kind: "badge",
    };
  }
  if (group === "wip")
    return { reason: null, severity: "neutral", kind: "badge" };
  if (job.close_state) {
    return {
      reason: CLOSE_STATE_LABEL[job.close_state],
      severity: "neutral",
      kind: "badge",
    };
  }

  if (job.needed_by_year_suspect) {
    return {
      reason: "date looks wrong — check in Tharstern",
      severity: "amber",
      kind: "hint",
    };
  }

  const dueDayDiff = job.required_at
    ? dueDayDiffFromToday(job.required_at)
    : null;
  const hasRedAlert = job.rag === "red";

  // Deadline-only: the date decides OVERDUE, full stop.
  if (dueDayDiff !== null && dueDayDiff < 0) {
    return {
      reason: `OVERDUE ${Math.abs(dueDayDiff)}d`,
      severity: "red",
      kind: "badge",
    };
  }
  if (dueDayDiff === 0) {
    // Due today keeps its own label even under a red alert — the alert
    // still escalates the dot/badge to red so it isn't hidden, but the
    // words never claim a job going out today is overdue.
    return {
      reason: "DUE TODAY",
      severity: hasRedAlert ? "red" : "amber",
      kind: "badge",
    };
  }
  if (dueDayDiff !== null && dueDayDiff > 0) {
    // A red alert still escalates severity (the dot/badge colour) — it
    // doesn't erase how soon the job is due, so the day count stays either
    // way; it just no longer mints its own "ALERT" word (see docblock).
    const reason =
      group === "due_today" || group === "due_soon"
        ? `DUE IN ${dueDayDiff}d`
        : `AT RISK · due in ${dueDayDiff}d`;
    return { reason, severity: hasRedAlert ? "red" : "amber", kind: "badge" };
  }
  // No due date at all — nothing for the deadline badge to say; a red alert
  // still escalates severity via the dot.
  return {
    reason: "AT RISK",
    severity: hasRedAlert ? "red" : "amber",
    kind: "badge",
  };
}

/** Days elapsed since an ISO timestamp, floored to whole days. */
function daysSince(iso: string): number {
  return Math.floor(
    (currentTime().getTime() - new Date(iso).getTime()) / 86_400_000,
  );
}

/** One reason line rendered below a card's badges. Priority order:
 *  1. `waiting_reason` — the human's own words, most specific.
 *  2. else `top_alert_label` — the top open work alert's own name.
 *  3. else nothing, unless `readiness_since` is set, in which case the line
 *     is worded off `readiness` instead (the two states mean different
 *     things: a not-production-ready job has never started, an
 *     out-for-proof job is sitting with the customer).
 *
 *  When a reason is present and `readiness_since` is also set, the line
 *  appends the time in state (`"Waiting for files · 20 days"`). Exported
 *  for the priority-order unit tests. */
export function boardReasonLine(job: WorkUnitSummaryItem): string | null {
  const days = job.readiness_since ? daysSince(job.readiness_since) : null;
  const reason = job.waiting_reason ?? job.top_alert_label ?? null;

  if (reason)
    return days !== null
      ? `${reason} · ${days} ${plural("day", days)}`
      : reason;
  if (days === null) return null;

  if (job.readiness === "not_production_ready") {
    return `Booked ${days} ${plural("day", days)} ago · nothing started`;
  }
  if (job.readiness === "out_for_proof") {
    return `With the customer ${days} ${plural("day", days)}`;
  }
  return null;
}

// Fixed width of the left-hand RAG-dot/readiness-badge gutter (see
// `BoardJobRow`), applied only when a badge actually renders there — see
// `showReadinessBadge`. Set by the longest badge text, "Not production
// ready" — measured at ~104px at the badge's own text-xs/px-2 sizing. The
// badge is allowed to wrap onto two lines inside this width rather than
// being truncated: a fixed gutter narrower than the longest label would
// otherwise have to clip it, which is worse than an occasional two-line
// badge. If a future label is added that's longer than "Not production
// ready", widen this rather than let it clip.
const _GUTTER_WIDTH_CLASS = "w-[104px]";

function BoardJobRow({
  job,
  group,
}: {
  job: WorkUnitSummaryItem;
  group: PortfolioBoardGroup;
}) {
  const flag = boardFlag(job, group);
  const reasonLine = boardReasonLine(job);
  const [composerOpen, setComposerOpen] = useState(false);
  // Suppress the badge when it would just restate the lane the card is
  // already in (see `READINESS_SHAPED_LANE_READINESS`) — e.g. every card in
  // "Not production ready" carries that readiness by construction, so a
  // three-line grey badge repeating the lane heading on every card is noise,
  // not information. Without a badge to show, the gutter collapses to just
  // the dot instead of leaving a wide blank column — safe because every
  // card within one lane makes the same suppression choice, so alignment
  // within the lane is unaffected.
  const showReadinessBadge =
    READINESS_SHAPED_LANE_READINESS[group] !== job.readiness &&
    readinessBadgeVisible(job.readiness, job.work_kind);

  return (
    <div>
      {/* The row navigates on click of the text area; "Draft update" is a
       *  non-nested sibling button so we never put a button inside the
       *  Link's interactive tree. */}
      <div className="flex items-start gap-2 p-3 border border-border rounded-lg bg-card hover:bg-surface-hover transition-colors text-sm">
        <Link
          to={job.self_url}
          className="flex items-start gap-3 flex-1 min-w-0"
        >
          {/* Left gutter: RAG severity dot stacked directly above the
              readiness badge, one column the eye reads straight down —
              replaces the old inline placement after the title, which used
              to push the title text around and wrap every card at a
              different point. */}
          <div
            className={`flex flex-col items-center gap-1 shrink-0 pt-0.5 ${
              showReadinessBadge ? _GUTTER_WIDTH_CLASS : ""
            }`}
          >
            <span
              className={`size-2.5 rounded-full shrink-0 ${SEVERITY_DOT[flag.severity]}`}
              title={flag.reason ?? job.rag}
            />
            {showReadinessBadge && (
              <ReadinessBadge
                readiness={job.readiness}
                workKind={job.work_kind}
                className="whitespace-normal text-center leading-tight"
              />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-0.5">
              <span className="font-medium truncate">
                <span className="tabular-nums text-muted-foreground">
                  #{job.job_no}
                </span>{" "}
                {job.customer_name} — {job.job_title}
              </span>
              {/* Why this job is on the board at all, when the customer is
                  someone else's. Neutral, not a severity colour — the card's
                  other badges mean "act on this", and this one only explains
                  an unfamiliar customer name. */}
              {job.booked_by_you && (
                <Badge variant="neutral" className="shrink-0">
                  Booked in by you
                </Badge>
              )}
              <VisibilityProvenanceBadge
                visibilityReason={job.visibility_reason}
                coveringForUserName={job.covering_for_user_name}
                coverUntil={job.cover_until}
                className="shrink-0"
              />
              {flag.reason &&
                (flag.kind === "hint" ? (
                  <span className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-300">
                    <TriangleAlert className="size-3.5" />
                    {flag.reason}
                  </span>
                ) : (
                  <Badge
                    variant={
                      flag.severity === "red"
                        ? "error"
                        : flag.severity === "amber"
                          ? "warning"
                          : "neutral"
                    }
                    className="shrink-0 uppercase"
                  >
                    {flag.reason}
                  </Badge>
                ))}
            </div>
            {reasonLine && (
              <div className="text-xs text-muted-foreground mb-0.5">
                {reasonLine}
              </div>
            )}
            <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
              {job.required_at && (
                <span className="inline-flex items-center gap-1">
                  Needed by {formatDateWithYear(job.required_at)}
                </span>
              )}
              {job.quantity != null && (
                <span className="inline-flex items-center gap-1 tabular-nums">
                  Qty {job.quantity.toLocaleString()}
                </span>
              )}
              {(job.email_count > 0 || job.unread_email_count > 0) && (
                <span className="inline-flex items-center gap-1">
                  <Mail className="size-3" />
                  {job.email_count}
                  {job.unread_email_count > 0 && (
                    <span className="text-primary font-medium">
                      · {job.unread_email_count} unread
                    </span>
                  )}
                </span>
              )}
              {job.meeting_count > 0 && (
                <span className="inline-flex items-center gap-1">
                  <CalendarDays className="size-3" />
                  {job.meeting_count} mtg
                </span>
              )}
            </div>
          </div>
        </Link>
        <Button
          size="sm"
          variant="ghost"
          className="shrink-0 mt-0.5"
          onClick={() => setComposerOpen(true)}
        >
          <Pencil className="size-3.5" />
          Draft update
        </Button>
      </div>
      <ReplyComposer
        job={{
          jobNo: job.job_no,
          jobTitle: job.job_title,
          customerName: job.customer_name,
          customerId: job.customer_id,
          requiredAt: job.required_at,
          rag: job.rag,
        }}
        open={composerOpen}
        onOpenChange={setComposerOpen}
      />
    </div>
  );
}
